import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

// Matches backend /catalog/funnel response shape (Topics 2+3+4+5+6 iteration)
export interface FunnelData {
  funnel: {
    catalog_products: number;
    // D11: AEO candidate union segments
    aeo_candidate_total: number;
    aeo_candidate_feed_only: number;
    aeo_candidate_manifest_only: number;
    aeo_candidate_feed_and_manifest: number;
    // D26
    not_in_aeo_count: number;
    enriched: number;
    aeo_generated: number;
    // D12: not-generated breakdown
    not_generated_breakdown: {
      in_queue: number;
      needs_attention?: number;
      awaiting_enrichment: number;
      failed: number;
      other: number;
    };
    approved: number;
    // D13: not-approved breakdown
    not_approved_breakdown: {
      in_review_queue: number;
      rejected: number;
      quality_flagged: number;
      archived?: number;
      other: number;
    };
    // D14: live/generated
    live: number;
    live_pct_vs_generated: number;
    // D25: site config
    target_language_count: number;
    sections_per_page: number;
    // canonical deduplication flag (new field)
    canonical_mode?: boolean;
    // legacy compat — present on older backend deployments
    in_active_feed?: number;
    has_translations?: number;
    pfi_universe?: number;
    retained_after_delisting?: number;
  };
  diff_bar: {
    completed_at: string;
    summary: {
      new?: number;
      updated?: number;
      reactivated?: number;
      unchanged?: number;
      unavailable?: number;
      [key: string]: unknown;
    };
  } | null;
  languages: Array<{ language: string; completed: number; total?: number }>;
}

export interface AggregateRow {
  group_key: string;
  group_key2?: string;
  // D18: renamed active → catalog_products; added aeo_candidates + per-stage %
  catalog_products: number;
  active?: number; // legacy compat
  aeo_candidates: number;
  enriched: number;
  aeo_generated: number;
  approved: number;
  live: number;
  live_pct: number;
  pct_aeo_generated: number;
  pct_approved: number;
  pct_live: number;
  // per-row translation count — rows whose AEO content has fully translated to all target languages
  fully_translated?: number;
}

// Aggregate endpoint response: { rows, group, group2, target_language_count }
export interface AggregateResponse {
  rows: AggregateRow[];
  group?: string;
  group2?: string | null;
  target_language_count?: number;
}

export interface CanonicalRow {
  pfi_id: string;
  product_id: string;
  product_name: string;
  brand: string;
  category: string;
  custom_label_4: string | null;
  product_url: string | null;
  image_url?: string | null;
  // D19: 4-way source classifier
  source: 'in_feed_scope' | 'retained_published' | 'out_of_scope' | 'feed_and_manifest' | 'feed_only' | 'manifest_only' | 'not_in_aeo';
  kbe_id: string | null;
  approval_status: string | null;
  aeo_id: string | null;
  aeo_status: string | null;
  approved_at: string | null;
  published_at: string | null;
  aeo_created_at: string | null;
  completed_languages: number;
  is_live_on_site: boolean;
  updated_at: string;
}

export interface CanonicalPage {
  rows: CanonicalRow[];
  next_cursor: string | null;
  total_estimate: number;
}

export type CatalogDimension =
  | 'category' | 'custom_label_4' | 'brand' | 'source'
  | 'aeo_status' | 'live_status' | 'translation_coverage_bucket' | 'updated_bucket';

export interface CatalogFilters {
  [key: string]: string | undefined;
}

// FIX-4: normalised client-side shape derived from raw traceability response
export interface UrlInspectResult {
  normalized_url: string;
  match_strategy: 'exact_normalized' | 'path_only' | 'slug' | 'not_found';
  stripped_params: string[];
  overall_status: 'healthy' | 'has_gaps' | 'broken';
  confidence: number;
  product_card: {
    product_id: string;
    pfi_id?: string;
    product_name: string;
    brand?: string;
    image_url?: string | null;
    gtin?: string | null;
    mpn?: string | null;
    category?: string | null;
    custom_label_4?: string | null;
    product_url?: string | null;
    feed_status?: string;
    source: string;
    cross_site_occurrences: Array<{
      site_id: string;
      site_name: string;
      url: string;
      source?: string;
      is_live_on_site?: boolean;
    }>;
  } | null;
  signals: Array<{
    stage: number;
    label: string;
    status: 'ok' | 'warn' | 'error' | 'missing';
    summary: string;
    detail: Record<string, unknown>;
    latency_ms?: number;
    drill_url?: string;
    drill_label?: string;
  }>;
  diagnostics: {
    schema_version: string;
    queried_at: string;
    query_latency_ms: number;
    match_strategy: string;
    signal_latency_ms?: Record<string, number>;
  };
}

// ─── Transform helpers (FIX-4) ────────────────────────────────────────────────

function deriveOverallStatus(signals: UrlInspectResult['signals']): 'healthy' | 'has_gaps' | 'broken' {
  if (signals.some(s => s.status === 'error')) return 'broken';
  if (signals.some(s => s.status === 'warn' || s.status === 'missing')) return 'has_gaps';
  return 'healthy';
}

function deriveConfidence(matchStrategy: string): number {
  const MAP: Record<string, number> = {
    exact_normalized: 1.0,
    path_only: 0.85,
    slug: 0.65,
    not_found: 0,
  };
  return MAP[matchStrategy] ?? 0.5;
}

// Maps raw traceability route response → normalised UrlInspectResult.
// Route returns: { match_strategy, normalized_url, stripped_params, signals }
// where signals is the fetchAllSignals() result object.
export function normaliseTraceabilityResponse(raw: Record<string, unknown>, startMs: number): UrlInspectResult {
  const matchStrategy = (raw.match_strategy as string) ?? 'not_found';

  // FIX-1: signal fields live under raw.signals, not on raw directly
  const payload = (raw.signals !== null && typeof raw.signals === 'object' && !Array.isArray(raw.signals))
    ? raw.signals as Record<string, unknown>
    : {} as Record<string, unknown>;

  const SIGNAL_KEYS: Array<[string, string]> = [
    ['product_identification', 'Product identification'],
    ['feed_presence',          'Feed presence'],
    ['import_status',          'Import status'],
    ['enrichment_status',      'Enrichment (KBE)'],
    ['canonical_selection',    'Canonical selection'],
    ['aeo_generation',         'AEO generation'],
    ['languages',              'Translations'],
    ['fields_generated',       'Fields generated'],
    ['publish_status',         'Publish status'],
    ['cross_site_occurrences', 'Cross-site occurrences'],
    ['analytics_ingestion',    'Analytics ingestion'],
  ];

  // Explicit allow-lists so failed/not_configured/configured_no_data don't become green
  const OK_STATUSES    = new Set(['active', 'ok', 'found', 'published', 'approved', 'matched', 'generated', 'completed']);
  const WARN_STATUSES  = new Set(['configured_no_data', 'not_configured', 'pending', 'pending_approval', 'partial', 'no_match']);
  const ERROR_STATUSES = new Set(['failed', 'error', 'not_found', 'broken']);

  function classifyStatus(s: unknown): 'ok' | 'warn' | 'error' | 'missing' {
    if (typeof s !== 'string') return 'missing';
    if (OK_STATUSES.has(s))    return 'ok';
    if (ERROR_STATUSES.has(s)) return 'error';
    if (WARN_STATUSES.has(s))  return 'warn';
    return 'warn'; // unknown status → warn, never silently ok
  }

  // analytics_ingestion has nested { gsc, ga4, google_ads, merchant_center }
  // each with their own status — aggregate across surfaces.
  // merchant_center also has approval_status: 'disapproved' | 'pending' which must be checked.
  function classifyAnalytics(obj: Record<string, unknown>): 'ok' | 'warn' | 'error' | 'missing' {
    const surfaces = ['gsc', 'ga4', 'google_ads', 'merchant_center'];
    const surfaceObjs = surfaces
      .map(k => obj[k])
      .filter((v): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v));

    if (surfaceObjs.length === 0) return 'missing';

    const statuses = surfaceObjs.map(v => classifyStatus(v.status));

    // merchant_center: approval_status overrides — disapproved → error, pending → at least warn
    const mc = obj.merchant_center as Record<string, unknown> | undefined;
    if (mc?.approval_status === 'disapproved') statuses.push('error');
    else if (mc?.approval_status === 'pending')  statuses.push('warn');

    if (statuses.some(s => s === 'error')) return 'error';
    if (statuses.some(s => s === 'warn' || s === 'missing')) return 'warn';
    return 'ok';
  }

  // Arrays whose items have a status field aggregate item statuses (error > warn > ok).
  // Arrays without status fields (e.g. cross_site_occurrences) are ok when non-empty.
  function classifyArray(arr: unknown[]): 'ok' | 'warn' | 'error' | 'missing' {
    if (arr.length === 0) return 'ok';
    const itemsWithStatus = arr
      .filter((item): item is Record<string, unknown> =>
        item !== null && typeof item === 'object' && !Array.isArray(item) && 'status' in (item as object)
      );
    if (itemsWithStatus.length === 0) return 'ok'; // non-status array (e.g. cross_site_occurrences)
    const itemStatuses = itemsWithStatus.map(item => classifyStatus(item.status));
    if (itemStatuses.some(s => s === 'error')) return 'error';
    if (itemStatuses.some(s => s === 'warn' || s === 'missing')) return 'warn';
    return 'ok';
  }

  // fields_generated: array of { field, present: boolean, value_summary }.
  // No status key — missing fields have present: false.
  function classifyFieldsGenerated(arr: unknown[]): 'ok' | 'warn' | 'error' | 'missing' {
    if (arr.length === 0) return 'missing';
    const items = arr.filter((item): item is Record<string, unknown> =>
      item !== null && typeof item === 'object' && !Array.isArray(item)
    );
    if (items.some(item => item.present === false)) return 'warn';
    return 'ok';
  }

  const signals: UrlInspectResult['signals'] = SIGNAL_KEYS.map(([key, label], idx) => {
    const val = payload[key];
    let status: 'ok' | 'warn' | 'error' | 'missing' = 'missing';
    let summary = 'Not available';

    if (val !== null && val !== undefined) {
      if (Array.isArray(val)) {
        status = key === 'fields_generated' ? classifyFieldsGenerated(val) : classifyArray(val);
        summary = val.length > 0 ? `${val.length} item(s)` : 'None';
      } else if (typeof val === 'object') {
        const obj = val as Record<string, unknown>;
        if (obj.error) {
          status = 'error';
          summary = String(obj.error);
        } else if (key === 'analytics_ingestion') {
          status = classifyAnalytics(obj);
          summary = `gsc:${classifyStatus((obj.gsc as Record<string, unknown>)?.status)} ga4:${classifyStatus((obj.ga4 as Record<string, unknown>)?.status)}`;
        } else {
          const rawStatus = obj.status ?? obj.aeo_status ?? null;
          if (rawStatus === null) {
            // No status field — data object, presence = ok (e.g. product_identification)
            status = 'ok';
            summary = label;
          } else {
            status = classifyStatus(rawStatus);
            summary = String(rawStatus ?? obj.summary ?? label);
          }
        }
      } else {
        status = classifyStatus(val);
        summary = String(val);
      }
    }

    return { stage: idx + 1, label, status, summary, detail: (val ?? {}) as Record<string, unknown> };
  });

  const pid = payload.product_identification as Record<string, unknown> | null | undefined;
  const crossSiteRaw = payload.cross_site_occurrences;
  const product_card = pid ? {
    product_id:    String(pid.product_id ?? pid.pfi_id ?? ''),
    pfi_id:        pid.pfi_id ? String(pid.pfi_id) : undefined,
    product_name:  String(pid.product_name ?? ''),
    brand:         pid.brand ? String(pid.brand) : undefined,
    image_url:     pid.image_url ? String(pid.image_url) : null,
    gtin:          pid.gtin ? String(pid.gtin) : null,
    mpn:           pid.mpn ? String(pid.mpn) : null,
    category:      pid.category ? String(pid.category) : null,
    custom_label_4:pid.custom_label_4 ? String(pid.custom_label_4) : null,
    product_url:   pid.product_url ? String(pid.product_url) : null,
    feed_status:   pid.feed_status ? String(pid.feed_status) : undefined,
    source:        String(pid.source ?? pid.feed_status ?? 'unknown'),
    cross_site_occurrences: Array.isArray(crossSiteRaw)
      ? crossSiteRaw.map((o: Record<string, unknown>) => ({
          site_id:        String(o.site_id ?? ''),
          site_name:      String(o.site_name ?? o.site_id ?? ''),
          url:            String(o.product_url ?? o.url ?? ''),
          source:         o.source ? String(o.source) : undefined,
          is_live_on_site:typeof o.is_live_on_site === 'boolean' ? o.is_live_on_site : undefined,
        }))
      : [],
  } : null;

  // Per-signal latency from backend audit
  const signalLatency = payload.signal_latency_ms as Record<string, number> | undefined;

  return {
    normalized_url:  String(raw.normalized_url ?? ''),
    match_strategy:  matchStrategy as UrlInspectResult['match_strategy'],
    stripped_params: Array.isArray(raw.stripped_params) ? raw.stripped_params as string[] : [],
    overall_status:  deriveOverallStatus(signals),
    confidence:      deriveConfidence(matchStrategy),
    product_card,
    signals,
    diagnostics: {
      schema_version:    String(payload.schema_version ?? '1.0'),
      queried_at:        String(payload.queried_at ?? new Date().toISOString()),
      query_latency_ms:  Date.now() - startMs,
      match_strategy:    matchStrategy,
      signal_latency_ms: signalLatency,
    },
  };
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useCatalogFunnel(siteId: string | undefined) {
  return useQuery<FunnelData>({
    queryKey: ['catalog', 'funnel', siteId],
    queryFn: async () => {
      const res = await authFetch(`${API_URL}/sites/${siteId}/catalog/funnel`);
      if (!res.ok) throw new Error(`Funnel fetch failed: ${res.status}`);
      // FIX-1: response is { funnel, diff_bar, languages } — returned as-is, typed correctly
      return res.json() as Promise<FunnelData>;
    },
    enabled: !!siteId,
    staleTime: 60_000,
  });
}

export function useCatalogAggregate(
  siteId: string | undefined,
  group: CatalogDimension | undefined,
  group2: CatalogDimension | undefined,
  filters: CatalogFilters,
) {
  // FIX-2: separate group + group2 params, not comma-joined
  const params = new URLSearchParams();
  if (group) params.set('group', group);
  if (group2) params.set('group2', group2);
  for (const [k, v] of Object.entries(filters)) {
    if (v) params.set(`filter[${k}]`, v);
  }

  return useQuery<AggregateResponse>({
    queryKey: ['catalog', 'aggregate', siteId, group, group2, filters],
    queryFn: async () => {
      const res = await authFetch(`${API_URL}/sites/${siteId}/catalog/aggregate?${params}`);
      if (!res.ok) throw new Error(`Aggregate fetch failed: ${res.status}`);
      // Endpoint returns { rows, group, group2, target_language_count }
      return await res.json() as AggregateResponse;
    },
    enabled: !!siteId && !!group,
    staleTime: 60_000,
  });
}

export function useCatalogCanonicals(
  siteId: string | undefined,
  filters: CatalogFilters,
  sort: string,
  size = 100,
) {
  return useInfiniteQuery<CanonicalPage>({
    queryKey: ['catalog', 'canonicals', siteId, filters, sort, size],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ size: String(size) });
      if (sort) params.set('sort', sort);
      if (pageParam) params.set('cursor', pageParam as string);
      for (const [k, v] of Object.entries(filters)) {
        if (!v) continue;
        // q is a direct search param (ILIKE on name/brand/product_id), not a filter dimension
        if (k === 'q') params.set('q', v);
        else params.set(`filter[${k}]`, v);
      }
      const res = await authFetch(`${API_URL}/sites/${siteId}/catalog/canonicals?${params}`);
      if (!res.ok) throw new Error(`Canonicals fetch failed: ${res.status}`);
      // Response is already { rows, next_cursor, total_estimate } — correct shape
      return res.json() as Promise<CanonicalPage>;
    },
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!siteId,
    staleTime: 30_000,
  });
}

// Numbered-page variant of useCatalogCanonicals — uses single useQuery with page param.
// Backend currently supports cursor only; we encode { offset } in the cursor for the page model.
// When backend adds explicit ?offset=N, swap the cursor encoding for the raw offset.
export function useCatalogCanonicalsPaged(
  siteId: string | undefined,
  filters: CatalogFilters,
  sort: string,
  page: number,
  pageSize = 50,
) {
  return useQuery<CanonicalPage>({
    queryKey: ['catalog', 'canonicals-paged', siteId, JSON.stringify(filters), sort, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams({ size: String(pageSize) });
      if (sort) params.set('sort', sort);
      // Encode offset as base64 cursor so backend cursor logic accepts it
      const offset = (page - 1) * pageSize;
      if (offset > 0) {
        const cursor = btoa(JSON.stringify({ offset }));
        params.set('cursor', cursor);
      }
      for (const [k, v] of Object.entries(filters)) {
        if (!v) continue;
        if (k === 'q') params.set('q', v);
        else params.set(`filter[${k}]`, v);
      }
      const res = await authFetch(`${API_URL}/sites/${siteId}/catalog/canonicals?${params}`);
      if (!res.ok) throw new Error(`Canonicals fetch failed: ${res.status}`);
      return res.json() as Promise<CanonicalPage>;
    },
    enabled: !!siteId,
    staleTime: 30_000,
  });
}

// Filter options for chip dropdowns — per-dimension distinct values.
export interface CatalogFilterOptions {
  category: string[];
  custom_label_4: string[];
  brand: string[];
  source: string[];
  aeo_status: string[];
  live_status: string[];
  translation_coverage_bucket: string[];
  updated_bucket?: string[];
}

export function useCatalogFilterOptions(siteId: string | undefined) {
  return useQuery<CatalogFilterOptions>({
    queryKey: ['catalog', 'filter-options', siteId],
    queryFn: async () => {
      const res = await authFetch(`${API_URL}/sites/${siteId}/catalog/filter-options`);
      if (!res.ok) throw new Error(`filter-options fetch failed: ${res.status}`);
      return res.json() as Promise<CatalogFilterOptions>;
    },
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });
}

export function useUrlInspect(url: string | undefined, siteId: string | undefined) {
  return useQuery<UrlInspectResult>({
    queryKey: ['catalog', 'inspect', url, siteId],
    queryFn: async () => {
      const startMs = Date.now();
      const params = new URLSearchParams({ url: url! });
      if (siteId) params.set('site_id', siteId);
      const res = await authFetch(`${API_URL}/traceability/url?${params}`);
      if (!res.ok) throw new Error(`URL inspect failed: ${res.status}`);
      // FIX-4: transform raw traceability response → normalised UrlInspectResult
      const raw = await res.json() as Record<string, unknown>;
      return normaliseTraceabilityResponse(raw, startMs);
    },
    enabled: !!url,
    staleTime: 300_000,
    retry: 1,
  });
}
